<template>
  <el-dialog
    title="批量办理异动"
    :visible.sync="dialogVisible"
    width="640px"
    append-to-body
    :close-on-click-modal="false"
    @closed="onClosed">
    <el-alert
      title="将一批在读学生办理为休学、转学或退学，办理后进入本页转退学生列表。"
      type="info"
      :closable="false"
      show-icon
      style="margin-bottom: 14px;" />
    <el-form label-width="110px">
      <el-form-item label="异动类型" required>
        <el-radio-group v-model="form.targetStatus">
          <el-radio :label="1">休学</el-radio>
          <el-radio :label="2">转学</el-radio>
          <el-radio :label="3">退学</el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="匹配方式">
        <el-radio-group v-model="form.identifierType">
          <el-radio label="StudentId">按学生ID</el-radio>
          <el-radio label="StuCode">按学号</el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item :label="form.identifierType === 'StuCode' ? '学号列表' : '学生ID列表'" required>
        <el-input
          v-model="form.studentIdsText"
          type="textarea"
          :rows="8"
          :placeholder="form.identifierType === 'StuCode'
            ? '请输入学号，支持“学号,姓名”一行一条（例如：231236,张三）'
            : '请输入学生ID，支持一行一个，或使用逗号/中文逗号/分号/空格分隔'" />
      </el-form-item>
      <el-form-item label="办理原因" required>
        <el-input v-model="form.reason" type="textarea" :rows="2" placeholder="请填写办理原因" />
      </el-form-item>
      <el-form-item v-if="form.targetStatus !== 1" label="账户余额">
        <el-checkbox v-model="form.confirmBalance">账户仍有余额的学生也继续办理（余额另行处理）</el-checkbox>
        <div class="batch-balance-tip">不勾选时，有余额的学生会办理失败并列出金额，请先退费；迁出后后台无法再直接退费。</div>
      </el-form-item>
    </el-form>

    <div v-if="result" class="batch-result">
      <el-alert
        :title="`处理完成：共 ${result.Total || 0} 条，成功 ${result.SuccessCount || 0} 条，失败 ${result.FailCount || 0} 条`"
        :type="(result.FailCount || 0) > 0 ? 'warning' : 'success'"
        :closable="false" />
      <el-button
        v-if="(result.FailCount || 0) > 0"
        type="text"
        class="batch-result__link"
        @click="failedDialogVisible = true">
        查看失败明细
      </el-button>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="dialogVisible = false">取消</el-button>
      <el-button type="primary" :loading="submitting" @click="submit">确定办理</el-button>
    </span>

    <el-dialog
      title="批量异动失败明细"
      :visible.sync="failedDialogVisible"
      width="760px"
      append-to-body>
      <el-table :data="failedItems" border stripe size="small" max-height="420" empty-text="暂无失败记录">
        <el-table-column prop="InputValue" label="输入值" min-width="140" show-overflow-tooltip />
        <el-table-column prop="StudentId" label="学生ID" width="120" show-overflow-tooltip />
        <el-table-column prop="StuCode" label="学号" width="110" show-overflow-tooltip />
        <el-table-column prop="StudentName" label="姓名" width="100" show-overflow-tooltip />
        <el-table-column prop="Message" label="失败原因" min-width="180" show-overflow-tooltip />
      </el-table>
    </el-dialog>
  </el-dialog>
</template>

<script>
import { batchChangeEnrollmentStatus } from '../api/modules/studentEnrollment';

const ACTION_MAP = { 1: '休学', 2: '转学', 3: '退学' };

export default {
  name: 'BatchEnrollmentStatusDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    schoolDepartmentId: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      submitting: false,
      result: null,
      failedDialogVisible: false,
      form: {
        targetStatus: 2,
        identifierType: 'StudentId',
        studentIdsText: '',
        reason: '批量办理异动',
        confirmBalance: false
      }
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit('update:visible', val);
      }
    },
    failedItems() {
      const items = (this.result && this.result.Items) || [];
      return items.filter(item => !item.IsSuccess);
    }
  },
  watch: {
    visible(val) {
      if (val) this.resetForm();
    }
  },
  methods: {
    resetForm() {
      this.submitting = false;
      this.result = null;
      this.failedDialogVisible = false;
      this.form = {
        targetStatus: 2,
        identifierType: 'StudentId',
        studentIdsText: '',
        reason: '批量办理异动',
        confirmBalance: false
      };
    },
    onClosed() {
      if ((this.result && this.result.SuccessCount) > 0) {
        this.$emit('success');
      }
    },
    parseStudentIds() {
      const raw = this.form.studentIdsText || '';
      const parts = raw.split(/[\s,，;；]+/g).map(v => (v || '').trim()).filter(Boolean);
      const numIds = parts.filter(v => /^\d+$/.test(v)).map(v => Number(v));
      return Array.from(new Set(numIds));
    },
    parseStuCodeItems() {
      const raw = this.form.studentIdsText || '';
      const lines = raw.split(/\r?\n/g).map(v => (v || '').trim()).filter(Boolean);
      const rows = [];
      lines.forEach((line) => {
        const cols = line.split(/[,\t，]+/g).map(v => (v || '').trim()).filter(Boolean);
        if (cols.length >= 2) {
          rows.push({ StuCode: cols[0], StudentName: cols[1] });
          return;
        }
        const bySpace = line.split(/\s+/g).map(v => (v || '').trim()).filter(Boolean);
        if (bySpace.length >= 2) {
          rows.push({ StuCode: bySpace[0], StudentName: bySpace[1] });
          return;
        }
        if (line) rows.push({ StuCode: line, StudentName: '' });
      });
      const map = {};
      rows.forEach((item) => {
        const key = `${item.StuCode}__${item.StudentName || ''}`.toLowerCase();
        if (!map[key]) map[key] = item;
      });
      return Object.values(map).filter(item => !!item.StuCode);
    },
    submit() {
      if (!this.schoolDepartmentId) {
        this.$message.warning('请先在顶部选择学校');
        return;
      }
      if (![1, 2, 3].includes(Number(this.form.targetStatus))) {
        this.$message.warning('请选择异动类型');
        return;
      }
      const reason = String(this.form.reason || '').trim();
      if (!reason) {
        this.$message.warning('请填写办理原因');
        return;
      }

      const isStuCodeMode = this.form.identifierType === 'StuCode';
      const stuCodeItems = isStuCodeMode ? this.parseStuCodeItems() : [];
      const studentIds = isStuCodeMode ? [] : this.parseStudentIds();
      const count = isStuCodeMode ? stuCodeItems.length : studentIds.length;
      if (!count) {
        this.$message.error(isStuCodeMode ? '请输入至少一个有效学号' : '请输入至少一个有效学生ID');
        return;
      }

      const actionName = ACTION_MAP[this.form.targetStatus] || '异动';
      this.$confirm(`确认将 ${count} 位学生批量办理为【${actionName}】？办理后将离开在读花名册。`, '提示', {
        type: 'warning'
      }).then(() => {
        this.submitting = true;
        const payload = {
          SchoolDepartmentId: this.schoolDepartmentId,
          IdentifierType: this.form.identifierType,
          TargetStatus: this.form.targetStatus,
          Reason: reason,
          ConfirmBalance: this.form.targetStatus !== 1 && this.form.confirmBalance
        };
        if (isStuCodeMode) {
          payload.StuCodeItems = stuCodeItems;
          payload.StuCodes = stuCodeItems.map(item => item.StuCode);
        } else {
          payload.StudentIds = studentIds;
        }
        return batchChangeEnrollmentStatus(payload);
      }).then((res) => {
        this.submitting = false;
        if (res && res.data && res.data.success) {
          this.result = res.data.response || {};
          this.$message({
            message: res.data.msg || '办理完成',
            type: (this.result.FailCount || 0) > 0 ? 'warning' : 'success',
            duration: 5000
          });
          if ((this.result.SuccessCount || 0) > 0 && (this.result.FailCount || 0) === 0) {
            this.dialogVisible = false;
            this.$emit('success');
          }
          return;
        }
        this.$message.error((res && res.data && res.data.msg) || '办理失败');
      }).catch((err) => {
        if (err !== 'cancel') this.submitting = false;
      });
    }
  }
};
</script>

<style scoped>
.batch-result {
  margin-top: 10px;
}

.batch-result__link {
  margin-top: 6px;
  padding-left: 0;
}

.batch-balance-tip {
  color: #909399;
  font-size: 12px;
  line-height: 18px;
}
</style>
